import React, { useMemo } from 'react';
import { GetServerSideProps } from 'next';
import { Theme } from '@/components/Theme/Theme';
import { Header } from '@/components/Header/Header';
import { Footer } from '@/components/Footer/Footer';
import { ServerDataType } from '@/types/publishedAssets';
import { PageContext } from '@/context/PageContext';
import type { PageHostnames } from '@/customDomains/types';
import { useRouter } from 'next/router';
import { useLocalization } from 'utils/useLocalization';
import '@/i18n';
import { useIsEmbedSetup } from 'utils/useIsEmbedSetup';
import { logRequest } from 'utils/consoleLog';
import { useDatadogBrowser } from 'utils/useDatadogBrowser';
import { SupportedLanguages } from 'types/supportedLanguages';
import { getAllPagesForOrganization } from 'utils/getAllPagesForOrganization';
import { publicEnv, setPublicEnvAsGlobal } from 'utils/publicEnv';
import { correctLocaleCasing } from 'utils/correctLocaleCasing';
import { Sitemap } from 'screens/sitemap/Sitemap';
import { SitemapPageResponse } from 'src/service/engageFrontendGateway';
import { getHomeThemeWithLanguages } from 'src/serverUtils/utils';
import { fetchFeatureFlags } from 'src/serverUtils/fetchFeatureFlags';
import { buildCanonicalSlug } from 'utils/buildCanonicalSlug';
import { resolvePageHostnames } from '@/customDomains/publicUrls';

export const getServerSideProps: GetServerSideProps = async ({ req, params }) => {
  // Fetch data from external API based on organization and locale
  logRequest(req);
  const organization = params?.organization as string;
  const localeRaw = params?.locale as string;
  const locale = correctLocaleCasing(localeRaw);
  const theme = await getHomeThemeWithLanguages({ organization, locale });

  const serverData = {
    theme,
  };
  // Generate the XML sitemap with the data
  const pages = await getAllPagesForOrganization(organization);
  const urlParams = {
    organization,
    locale,
  };

  const featureFlags = await fetchFeatureFlags(organization);
  return {
    props: {
      serverData,
      pages,
      urlParams,
      publicEnvVars: publicEnv,
      featureFlags,
      ...resolvePageHostnames(organization, req?.headers?.host, featureFlags),
    },
  };
};

export default function Index({
  serverData,
  urlParams,
  publicEnvVars,
  pages,
  canonicalHostname,
  linkHostname,
}: {
  serverData: ServerDataType;
  urlParams: { organization: string; locale: SupportedLanguages };
  publicEnvVars: typeof publicEnv;
  pages: SitemapPageResponse['items'];
} & PageHostnames) {
  const router = useRouter();
  useLocalization(urlParams.locale);
  setPublicEnvAsGlobal(publicEnvVars);
  useDatadogBrowser();
  useIsEmbedSetup();

  const contextValue = useMemo(
    () => ({
      theme: serverData.theme,
      urlParams,
      slug: buildCanonicalSlug(router),
      feeds: serverData.feeds,
      forceLanguageMenu: true,
      canonicalHostname,
      linkHostname,
    }),
    [serverData.theme, urlParams, router, serverData.feeds, canonicalHostname, linkHostname],
  );

  return (
    <PageContext.Provider value={contextValue}>
      {/** Render Header */}
      <Header />
      {/** Render Sitemap */}
      <Sitemap pages={pages} urlParams={urlParams} />
      {/** Render Footer */}
      <Footer />
      {/** Render Theme */}
      <Theme theme={serverData.theme} />
    </PageContext.Provider>
  );
}
